import { atom } from 'jotai';

import type { PendingAttachment } from '../types';

import {
  $isSendingMessage,
  $selectedChatId,
  $sendMessage,
} from './chatConversation.state';

import { $user } from '~/shared/providers/auth/identity.localState';

export const $messageInput = atom<string>('');
export const $pendingAttachments = atom<PendingAttachment[]>([]);

export const $addAttachment = atom(
  null,
  (_get, set, attachment: PendingAttachment) => {
    set($pendingAttachments, (prev) => [...prev, attachment]);
  },
);

export const $updateAttachment = atom(
  null,
  (_get, set, update: { id: string } & Partial<PendingAttachment>) => {
    set($pendingAttachments, (prev) =>
      prev.map((a) => (a.id === update.id ? { ...a, ...update } : a)),
    );
  },
);

export const $removeAttachment = atom(null, (_get, set, id: string) => {
  set($pendingAttachments, (prev) => prev.filter((a) => a.id !== id));
});

export const $clearAttachments = atom(null, (_get, set) => {
  set($pendingAttachments, []);
});

export const $canSendMessage = atom((get) => {
  const input = get($messageInput).trim();
  const isSending = get($isSendingMessage);
  const hasSelectedChat = !!get($selectedChatId);
  const attachments = get($pendingAttachments);

  const hasContent = input.length > 0;
  const hasConfirmedAttachments = attachments.some(
    (a) => a.status === 'confirmed',
  );
  const hasUploading = attachments.some((a) => a.status === 'uploading');

  return (
    (hasContent || hasConfirmedAttachments) &&
    !isSending &&
    !hasUploading &&
    hasSelectedChat
  );
});

export const $submitActionResponse = atom(
  null,
  async (get, set, option: { label: string; value: string }) => {
    const user = get($user);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    await set($sendMessage, {
      content: option.label,
      firstName: user?.firstName,
      lastName: user?.lastName,
      timezone,
    });
  },
);

export const $submitMessage = atom(null, async (get, set) => {
  const content = get($messageInput).trim();
  const attachments = get($pendingAttachments);
  const confirmedFileIds = attachments
    .filter(
      (a): a is typeof a & { fileId: string } =>
        a.status === 'confirmed' && !!a.fileId,
    )
    .map((a) => a.fileId);

  if (!content && confirmedFileIds.length === 0) return;

  const user = get($user);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  set($messageInput, '');
  set($clearAttachments);
  await set($sendMessage, {
    content: content || ' ',
    fileIds: confirmedFileIds.length > 0 ? confirmedFileIds : undefined,
    firstName: user?.firstName,
    lastName: user?.lastName,
    timezone,
  });
});
